import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";

interface NavbarProps {
  scrollToSection: (sectionId: string) => void;
}

const serviceItems = [
  "Infrastructure Planning",
  "Cybersecurity",
  "Cloud Solutions",
  "Digital Transformation",
  "IT Strategy",
  "Technical Support",
];

export const Navbar = ({ scrollToSection }: NavbarProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (section: string) => {
    scrollToSection(section);
    setIsMenuOpen(false);
    setIsServicesOpen(false);
  };

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-sm border-b border-slate-200/80 dark:border-slate-800/80 py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto max-w-7xl px-4 flex justify-between items-center">
        {/* Logo */}
        <button
          onClick={() => handleNav("home")}
          className="flex items-center gap-2 group"
        >
          <div className="w-9 h-9 rounded-lg bg-sky-500 flex items-center justify-center">
            <span className="text-white font-bold text-sm">LT</span>
          </div>
          <span className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">
            LUXA<span className="text-sky-500">-TECH</span>
          </span>
        </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          <button
            onClick={() => handleNav("home")}
            className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-sky-500 dark:hover:text-sky-400 transition-colors"
          >
            Home
          </button>

          {/* Services dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setIsServicesOpen(true)}
            onMouseLeave={() => setIsServicesOpen(false)}
          >
            <button
              onClick={() => handleNav("what-we-do")}
              className="flex items-center gap-1 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-sky-500 dark:hover:text-sky-400 transition-colors"
            >
              Services
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${isServicesOpen ? "rotate-180" : ""}`}
              />
            </button>
            {isServicesOpen && (
              <div className="absolute top-full left-0 mt-3 w-56 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 py-2 animate-in fade-in slide-in-from-top-2 duration-150">
                {serviceItems.map((s) => (
                  <button
                    key={s}
                    onClick={() => handleNav("what-we-do")}
                    className="w-full text-left px-4 py-2.5 text-sm text-slate-600 dark:text-slate-300 hover:bg-sky-50 dark:hover:bg-sky-500/10 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            onClick={() => handleNav("capabilities")}
            className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-sky-500 dark:hover:text-sky-400 transition-colors"
          >
            Capabilities
          </button>

          <button
            onClick={() => handleNav("about")}
            className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-sky-500 dark:hover:text-sky-400 transition-colors"
          >
            About
          </button>

          <Button
            onClick={() => handleNav("contact")}
            className="bg-sky-500 hover:bg-sky-600 text-white border-0 rounded-full px-6 h-9 text-sm font-medium shadow-md shadow-sky-500/20 transition-all hover:shadow-sky-500/30"
          >
            Contact Us
          </Button>
        </div>

        {/* Mobile burger */}
        <button
          className="md:hidden text-slate-700 dark:text-slate-300 p-1"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white/98 dark:bg-slate-900/98 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
          <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {["home", "what-we-do", "capabilities", "about"].map((s) => (
              <button
                key={s}
                onClick={() => handleNav(s)}
                className="text-left py-2.5 px-3 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-sky-500 transition-colors capitalize"
              >
                {s === "what-we-do" ? "Services" : s}
              </button>
            ))}
            <div className="pt-2">
              <Button
                onClick={() => handleNav("contact")}
                className="bg-sky-500 hover:bg-sky-600 text-white border-0 rounded-full w-full"
              >
                Contact Us
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
